import { HttpClient } from '@angular/common/http';
import { inject, Injector } from '@angular/core';
import { Router } from '@angular/router';

import { TranslateService } from '@ngx-translate/core';

import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { EMPTY, firstValueFrom, forkJoin, from, Observable, of, pipe, throwError } from 'rxjs';
import { catchError, finalize, map, switchMap, tap } from 'rxjs/operators';

import { ProductViewType } from '@ph-core/enums/product-view-type';
import { AuthFlowTraceService } from '@ph-core/services/auth/auth-flow-trace.service';
import {
  clearIdpSessionPreexisted,
  clearSsoTabState,
  readPostLogoutAll,
  recordDavinciSsoDefer,
} from '@ph-core/services/auth/auth-sso-defer';
import { TokenService } from '@ph-core/services/auth/token.service';
import { SloLogoutService } from '@ph-core/services/auth/slo-logout.service';
import { ConfigService } from '@ph-core/services/config/config.service';
import { PhDialog } from '@ph-core/services/dialog';
import { ErrorService } from '@ph-core/services/error/error.service';
import { LoggerContext } from '@ph-core/enums/logger-context.enum';
import { LoggerService } from '@ph-core/services/logger/logger.service';
import { OktaService } from '@ph-core/services/okta/okta.service';
import { RouterService } from '@ph-core/services/router/router.service';
import { withConditionalStorageSync } from '@ph-core/utils/with-conditional-storage-sync';
import { UserService } from 'app/home/features/profile/services/user.service';
import { ProductTypeMarkupEntry } from '@ph-model/api/response/dealer-defaults.response.model';
import { RecentQuotes } from '@ph-model/api/response/recent-quotes.response.model';
import { Markup, UserResponse } from '@ph-model/login';
import { AccessScope } from '@ph-model/login/access-scopes';
import { DavinciTokenRequest } from '@ph-model/request/davinci-token.request';
import { AvailableRole, CurrentUsers, GetUserResponse } from '@ph-model/response/getuser.response';
import { UpdatePasswordPopupComponent } from '@ph-shared/components/update-password/update-password-popup.component';
import { LoadQuote } from '@ph-shopping/model/cart';
import { ShoppingStepsUrls } from '@ph-shopping/model/shopping-step-urls.model';
import { SortOptionIdEnum } from '@ph-shopping/model/sort-option';
import {
  ShoppingStepperStore,
  ShoppingStepperStoreType,
} from '@ph-shopping/modules/shopping-stepper/store/shopping-stepper.store';
import { CartStoreRequestsDispatcher } from '@ph-shopping/store/cart/cart-store-requests.dispatcher';
import { mapProductsFromLoadQuote } from '@ph-shopping/store/cart/cart.helpers';
import { ProductStoreRequestsDispatcher } from '@ph-shopping/store/product/product-store-requests.dispatcher';
import { VehicleStoreRequestsDispatcher } from '@ph-shopping/store/vehicle/vehicle-store-requests.dispatcher';
import { ConfigStore, ConfigStoreType } from '@ph-store/config/config.store';
import { ContractSearchStore, ContractSearchStoreType } from '@ph-store/contract-search/contract-search.store';

function supportsDealerProductTypeMarkups(configService: ConfigService): boolean {
  return !!configService.environment.features.shopping.productTypeMarkups.length;
}

/** User-scoped markup excludes dealer product-type rows when that feature is enabled. */
function toUserScopedMarkup(markup: Markup, dealerProductTypeMarkups: boolean): Markup {
  if (!dealerProductTypeMarkups) {
    return markup;
  }
  return { ...markup, productTypeMarkup: [] };
}

const storeKey = 'UserStore';

export interface UserState {
  appVersion: string;
  emulatedDealer: boolean;
  error: string;
  isLoggedIn: boolean;
  markup: Markup;
  originalUser: UserResponse;
  pendingContractsCount: number;
  recentQuotes: RecentQuotes[];
  sessionExpired: boolean;
  success: boolean;
  user: UserResponse | undefined;
  // Dealer Admin
  users: CurrentUsers[];
  roles: AvailableRole[];
}

const InitialState: UserState = {
  appVersion: '',
  emulatedDealer: false,
  error: '',
  isLoggedIn: false,
  markup: new Markup(),
  originalUser: {} as UserResponse,
  pendingContractsCount: 0,
  recentQuotes: [],
  roles: [],
  sessionExpired: false,
  success: false,
  user: undefined,
  users: [],
};

export type UserStoreType = InstanceType<typeof UserStore>;

export const UserStore = signalStore(
  { providedIn: 'root' },
  withDevtools(storeKey),
  withConditionalStorageSync({ key: storeKey }),
  withState<UserState>(InitialState),
  withMethods(
    (
      store,
      configService = inject(ConfigService),
      configStore: ConfigStoreType = inject(ConfigStore),
      contractSearchStore: ContractSearchStoreType = inject(ContractSearchStore),
      errorService: ErrorService = inject(ErrorService),
      httpClient: HttpClient = inject(HttpClient),
      logger: LoggerService = inject(LoggerService),
      oktaService: OktaService = inject(OktaService),
      phDialogService: PhDialog = inject(PhDialog),
      router: Router = inject(Router),
      routerService: RouterService = inject(RouterService),
      shoppingStepperStore: ShoppingStepperStoreType = inject(ShoppingStepperStore),
      tokenService: TokenService = inject(TokenService),
      authFlowTrace: AuthFlowTraceService = inject(AuthFlowTraceService),
      translate: TranslateService = inject(TranslateService),
      userService: UserService = inject(UserService),
      productStoreRequestsDispatcher = inject(ProductStoreRequestsDispatcher),
      cartStoreRequestsDispatcher = inject(CartStoreRequestsDispatcher),
      vehicleStoreRequestsDispatcher = inject(VehicleStoreRequestsDispatcher),
      injector = inject(Injector)
    ) => {
      const applyProductTypeMarkupLocal = (productTypeMarkup: ProductTypeMarkupEntry[]): void => {
        const currentMarkup = store.markup();
        const markup: Markup = { ...currentMarkup, productTypeMarkup };
        const user = store.user();
        patchState(store, {
          markup,
          ...(user
            ? {
                user: {
                  ...user,
                  userDefaults: {
                    ...user.userDefaults,
                    markup: {
                      ...user.userDefaults.markup,
                      productTypeMarkup,
                    },
                  },
                },
              }
            : {}),
        });
        productStoreRequestsDispatcher.dispatchChangeMarkup(markup);
      };

      const extractDealerProductTypeMarkup = (
        response: { markup?: { productTypeMarkup?: ProductTypeMarkupEntry[] }; defaults?: { markup?: { productTypeMarkup?: ProductTypeMarkupEntry[] } } }
      ): ProductTypeMarkupEntry[] =>
        response.markup?.productTypeMarkup ??
        response.defaults?.markup?.productTypeMarkup ??
        [];

      const loadDealerProductTypeMarkups$ = (): Observable<void> => {
        if (!supportsDealerProductTypeMarkups(configService)) {
          return of(undefined);
        }
        return userService.getDealerDefaults().pipe(
          tap((response) => {
            applyProductTypeMarkupLocal(extractDealerProductTypeMarkup(response));
          }),
          map(() => undefined),
          catchError((error: unknown) => {
            logger.log(LoggerContext.UserStore, 'dealer-defaults request failed', error);
            return of(undefined);
          })
        );
      };
      const ssoDavinciLogin$ = (
        davinciTokenRequest: DavinciTokenRequest,
        options?: { tokenRefreshOnly?: boolean }
      ): Observable<UserResponse> => {
        const tokenRefreshOnly = options?.tokenRefreshOnly === true;
        const environment = configService.environment;
        logger.log(LoggerContext.Auth, 'ssoDavinciLogin request received', { tokenRefreshOnly });
        if (readPostLogoutAll(environment.application)) {
          logger.log(LoggerContext.Auth, 'ssoDavinciLogin skipped — post logout-all in progress');
          return throwError(() => new Error('logout-all-in-progress'));
        }
        if (tokenRefreshOnly) {
          configService.isManualDavinciTokenRefreshing.set(true);
        }
        const obs$ = environment.okta.config.SSOCredentials.idps.length
          ? httpClient.post<UserResponse>(`${environment.apiUrl}/okta/ssologin`, davinciTokenRequest)
          : httpClient.post<UserResponse>(`${environment.apiUrl}/okta/getOktaUser`, davinciTokenRequest);

        return obs$.pipe(
          switchMap((response: UserResponse) => {
            const responseCopy = { ...response };
            const tokenInspectionFromResponse = tokenService.inspectToken(response.token);
            tokenService.setToken(response.token);
            tokenService.setOriginalUserToken(response.token);
            authFlowTrace.record('post-ssologin-navigation', 'ssoDavinciLogin:token-cookie-set', {
              tokenRefreshOnly,
              tokenInspectionFromResponse,
              cookieAfterSet: tokenService.inspectToken(tokenService.getToken()),
            });
            // Removing token, so it would not be stored in localStorage, addressing feedback from PEN testers
            delete response.token;
            delete responseCopy.token;

            const dealerPtm = supportsDealerProductTypeMarkups(configService);
            const loginMarkup = toUserScopedMarkup(
              { ...response.userDefaults.markup },
              dealerPtm
            );
            const user = {
              ...response,
              scopes: response.menus.find((menu) => menu.route === 'claims')
                ? [...response.scopes, 'CLAIMS' as AccessScope]
                : response.scopes,
              siteInfo: {
                ...response.siteInfo,
                appEmail2: response.siteInfo.appEmail2 || 'dealersupport@adventureprotect.ca',
                dealerSupportPhoneNumber2: response.siteInfo.dealerSupportPhoneNumber2 || '833-954-0079',
              },
              userDefaults: {
                ...response.userDefaults,
                markup: loginMarkup,
              },
            };

            patchState(store, {
              user,
              originalUser: user,
              markup: loginMarkup,
              isLoggedIn: true,
              success: true,
            });

            if (!tokenRefreshOnly) {
              clearSsoTabState();
            }

            const pendingContractsCount = response.pendingContracts;
            if (pendingContractsCount) {
              patchState(store, { pendingContractsCount });
            }

            if (!tokenRefreshOnly) {
              if (environment.features.dynamicApplicationRedirects.length > 0) {
                if (response.application !== environment.application) {
                  const redirect = environment.features.dynamicApplicationRedirects.find(
                    (route) => route.application === response.application
                  );
                  if (redirect) {
                    window.open(redirect.url, '_self');

                    return of(responseCopy);
                  }
                }
              }
              const { userName } = response;
              const previousUser = localStorage.getItem('previousUser');
              if (previousUser !== userName) {
                vehicleStoreRequestsDispatcher.dispatchResetProductForm();
                productStoreRequestsDispatcher.dispatchResetProducts();
                shoppingStepperStore.resetShoppingProgress();
                cartStoreRequestsDispatcher.dispatchResetCart();
              }
              routerService.navigateToContent(user);

              const preferredLang = response.preferredLanguage;
              const cachedLang = localStorage.getItem('languageCode');
              if (preferredLang && preferredLang !== cachedLang) {
                localStorage.setItem('languageCode', preferredLang);
                translate.use(preferredLang);
                location.reload();
              }
            }

            return loadDealerProductTypeMarkups$().pipe(map(() => responseCopy));
          }),
          catchError((error: unknown) => {
            logger.log(LoggerContext.Auth, 'ssoDavinciLogin request failed');
            if (error instanceof Error && error.message === 'logout-all-in-progress') {
              return throwError(() => error);
            }
            if (tokenRefreshOnly) {
              return throwError(() => error);
            }

            // The SSO error page renders this failure; drop the interceptor toast synchronously,
            // before the async IdP check gives the app-level error effect a chance to push it.
            errorService.hideErrorMessage();

            return from(Promise.all([oktaService.hasIdpSession(), oktaService.isIncognitoBrowser()])).pipe(
              switchMap(([idpAlive, incognito]) => {
                // The origin-wide preexisted flag can outlive the Okta session (SLO iframe cannot
                // clear first-party storage); no live IdP session means no sibling to conflict with.
                // ponytail: incognito can't see the session, so keep the flag (set by silent SSO).
                // Ceiling: stale flag after the Okta session ended → false conflict; End all sessions clears it.
                if (!idpAlive && !incognito) {
                  clearIdpSessionPreexisted();
                }
                // ponytail: getOktaUser/ssologin failures → conflict UI; see recordDavinciSsoDefer.
                const defer = recordDavinciSsoDefer(
                  environment.application,
                  error,
                  environment.features.enforceSinglePortalSession
                );
                const errorMessage = defer.message || 'Unknown error';
                patchState(store, { success: false, error: errorMessage });
                errorService.displaySsoError(defer.scenario, defer.message);
                void router.navigate(['/callback/error']);

                return throwError(() => error);
              })
            );
          }),
          finalize(() => {
            if (tokenRefreshOnly) {
              configService.isManualDavinciTokenRefreshing.set(false);
            }
          }),
        );
      };

      const performSsoDavinciLoginImpl = (
        davinciTokenRequest: DavinciTokenRequest,
        options?: { tokenRefreshOnly?: boolean }
      ): Promise<UserResponse> => firstValueFrom(ssoDavinciLogin$(davinciTokenRequest, options));

      const clearLocalPortalSessionImpl = (options?: {
        preservePreviousUser?: boolean;
        preserveOktaTokens?: boolean;
      }): void => {
        const environment = configService.environment;
        const user = store.user();

        if (!options?.preservePreviousUser && user?.userName) {
          localStorage.setItem('previousUser', user.userName);
        }

        tokenService.deleteToken();
        tokenService.deleteOriginalUserToken();
        if (!options?.preserveOktaTokens) {
          oktaService.clearLocalOktaTokens();
        }
        clearSsoTabState();
        localStorage.removeItem('announcementPopupViewed');
        localStorage.removeItem(storeKey);

        if (!readPostLogoutAll(environment.application)) {
          errorService.hideErrorMessage();
        }
        contractSearchStore.resetContractSearchData();
        if (environment.features.shopping.resetDealInformationOnLogout) {
          vehicleStoreRequestsDispatcher.dispatchResetProductForm();
        }
        productStoreRequestsDispatcher.dispatchResetProducts();
        shoppingStepperStore.resetShoppingProgress();
        cartStoreRequestsDispatcher.dispatchResetCart();
        patchState(store, { ...InitialState });

        if (typeof store.clearStorage === 'function') {
          store.clearStorage();
        }
      };

      return {
        login: rxMethod<{
          username: string;
          password: string;
          captcha: string;
        }>(
          pipe(
            switchMap(({ username, password, captcha }) => {
              const programId = configStore.clientConfig?.programId && configStore.clientConfig?.programId();
              const environment = configService.environment;

              return httpClient
                .post<UserResponse>(`${environment.apiUrl}/login`, {
                  username,
                  password,
                  application: environment.application,
                  programId,
                  captcha,
                  captchaVersion: 'v3',
                })
                .pipe(
                  tapResponse<UserResponse>({
                    next: (response: UserResponse) => {
                      if (environment.features.profile.hasUserDefaults) {
                        configStore.toggleDealerView(
                          response.programDefaults?.dealerView === ProductViewType.Dealer
                        );
                        productStoreRequestsDispatcher.dispatchSortByTracker(
                          response.programDefaults?.sortBy ?? SortOptionIdEnum.ProductAsc
                        );
                      }
                      tokenService.setToken(response.token);
                      tokenService.setOriginalUserToken(response.token);
                      // Removing token, so it would not be stored in localStorage, addressing feedback from PEN testers
                      delete response.token;

                      const dealerPtm = supportsDealerProductTypeMarkups(configService);
                      const loginMarkup = toUserScopedMarkup(
                        { ...response.userDefaults.markup },
                        dealerPtm
                      );
                      const user = {
                        ...response,
                        scopes: response.menus.find((menu) => menu.route === 'claims')
                          ? [...response.scopes, 'CLAIMS' as AccessScope]
                          : response.scopes,
                        siteInfo: {
                          ...response.siteInfo,
                          appEmail2: response.siteInfo.appEmail2 || 'dealersupport@adventureprotect.ca',
                          dealerSupportPhoneNumber2: response.siteInfo.dealerSupportPhoneNumber2 || '833-954-0079',
                        },
                        userDefaults: {
                          ...response.userDefaults,
                          markup: loginMarkup,
                        },
                      };

                      patchState(store, {
                        user,
                        originalUser: user,
                        markup: loginMarkup,
                        isLoggedIn: true,
                        success: true,
                      });

                      if (environment.features.dynamicApplicationRedirects.length > 0) {
                        if (response.application !== environment.application) {
                          const redirect = environment.features.dynamicApplicationRedirects.find(
                            (route) => route.application === response.application
                          );
                          if (redirect) {
                            window.open(redirect.url, '_self');

                            return;
                          }
                        }
                      }
                      const { userName } = response;
                      const pendingContractsCount = response.pendingContracts;
                      const previousUser = localStorage.getItem('previousUser');
                      if (previousUser !== userName) {
                        vehicleStoreRequestsDispatcher.dispatchResetProductForm();
                        productStoreRequestsDispatcher.dispatchResetProducts();
                        shoppingStepperStore.resetShoppingProgress();
                        cartStoreRequestsDispatcher.dispatchResetCart();
                      }
                      routerService.navigateToContent(user);

                      if (pendingContractsCount) {
                        patchState(store, { pendingContractsCount });
                      }

                      void firstValueFrom(loadDealerProductTypeMarkups$());
                    },
                    error: (error: unknown) => {
                      console.error('Login response error: ', error);
                      const errorMessage =
                        (error && typeof error === 'object' && 'error' in error
                          ? (error as { error: any }).error
                          : error) || '';
                      patchState(store, { success: false, error: errorMessage });
                    },
                  })
                );
            })
          )
        ),

        performSsoDavinciLogin(
          davinciTokenRequest: DavinciTokenRequest,
          options?: { tokenRefreshOnly?: boolean }
        ): Promise<UserResponse> {
          return performSsoDavinciLoginImpl(davinciTokenRequest, options);
        },

        ssoDavinciLogin: rxMethod<{ davinciTokenRequest: DavinciTokenRequest }>(
          pipe(
            switchMap(({ davinciTokenRequest }) =>
              ssoDavinciLogin$(davinciTokenRequest).pipe(catchError(() => EMPTY))
            )
          )
        ),

        ssoValidateOktaPasswordResetToken: rxMethod<{ token: string }>(
          pipe(
            tap(({ token }) => {
              logger.log(LoggerContext.UserStore, 'resetpasswordvalid: rxMethod received token', {
                tokenLength: token?.length ?? 0,
                tokenSuffix: token ? token.slice(-4) : null,
                application: configService.environment.application,
                href: typeof window !== 'undefined' ? window.location.href : null,
              });
              authFlowTrace.record('password-reset', 'resetpasswordvalid:rxmethod-received', {
                tokenLength: token?.length ?? 0,
                tokenSuffix: token ? token.slice(-4) : null,
              });
            }),
            switchMap(({ token }) => {
              const environment = configService.environment;
              const url = `${environment.apiUrl}/okta/resetpasswordvalid/${token}`;
              const sanitizedUrl = `${environment.apiUrl}/okta/resetpasswordvalid/[redacted-${token?.slice(-4) ?? 'none'}]`;
              const startedAt = Date.now();
              const tokenMeta = {
                tokenLength: token?.length ?? 0,
                tokenSuffix: token ? token.slice(-4) : null,
                apiUrl: environment.apiUrl,
                application: environment.application,
                sanitizedUrl,
                method: 'GET',
                href: typeof window !== 'undefined' ? window.location.href : null,
              };

              logger.log(
                LoggerContext.UserStore,
                'resetpasswordvalid: issuing HttpClient.get (call is being made)',
                tokenMeta
              );
              authFlowTrace.record('password-reset', 'resetpasswordvalid:http-get-issued', tokenMeta);

              return httpClient.get<{ stateToken: string; username: string }>(url).pipe(
                tap({
                  next: (body) => {
                    logger.log(LoggerContext.UserStore, 'resetpasswordvalid: HttpClient emitted body', {
                      ...tokenMeta,
                      elapsedMs: Date.now() - startedAt,
                      username: body?.username,
                      hasStateToken: !!body?.stateToken,
                      stateTokenLength: body?.stateToken?.length ?? 0,
                    });
                    authFlowTrace.record('password-reset', 'resetpasswordvalid:http-body-received', {
                      ...tokenMeta,
                      elapsedMs: Date.now() - startedAt,
                      username: body?.username,
                      hasStateToken: !!body?.stateToken,
                    });
                  },
                  error: (error: unknown) => {
                    const httpError =
                      error && typeof error === 'object'
                        ? (error as { status?: number; statusText?: string; message?: string; error?: unknown })
                        : null;
                    logger.warn(LoggerContext.UserStore, 'resetpasswordvalid: HttpClient error emission', {
                      ...tokenMeta,
                      elapsedMs: Date.now() - startedAt,
                      status: httpError?.status,
                      statusText: httpError?.statusText,
                      message: httpError?.message,
                      errorBody: httpError?.error,
                    });
                    authFlowTrace.record('password-reset', 'resetpasswordvalid:http-error-emission', {
                      ...tokenMeta,
                      elapsedMs: Date.now() - startedAt,
                      status: httpError?.status,
                      errorBody: httpError?.error,
                    });
                  },
                }),
                tapResponse<{ stateToken: string; username: string }>({
                  next: ({ stateToken, username }) => {
                    logger.log(
                      LoggerContext.UserStore,
                      'resetpasswordvalid: success — opening update password popup',
                      {
                        ...tokenMeta,
                        elapsedMs: Date.now() - startedAt,
                        username,
                        hasStateToken: !!stateToken,
                        stateTokenLength: stateToken?.length ?? 0,
                      }
                    );
                    authFlowTrace.record('password-reset', 'resetpasswordvalid:success-open-popup', {
                      ...tokenMeta,
                      elapsedMs: Date.now() - startedAt,
                      username,
                      hasStateToken: !!stateToken,
                    });
                    phDialogService.open(UpdatePasswordPopupComponent, {
                      data: { stateToken, username, flowType: 'OKTA', actionType: 'RESET' },
                    });
                    logger.log(LoggerContext.UserStore, 'resetpasswordvalid: phDialogService.open invoked', {
                      ...tokenMeta,
                      username,
                    });
                    authFlowTrace.record('password-reset', 'resetpasswordvalid:dialog-open-invoked', {
                      ...tokenMeta,
                      username,
                    });
                  },
                  error: (error: unknown) => {
                    const httpError =
                      error && typeof error === 'object'
                        ? (error as { status?: number; statusText?: string; message?: string; error?: unknown })
                        : null;
                    const errorMessage =
                      (httpError && 'error' in httpError ? httpError.error : error) || '';
                    logger.warn(LoggerContext.UserStore, 'resetpasswordvalid: request failed (tapResponse)', {
                      ...tokenMeta,
                      elapsedMs: Date.now() - startedAt,
                      status: httpError?.status,
                      statusText: httpError?.statusText,
                      message: httpError?.message,
                      errorMessage,
                    });
                    authFlowTrace.record('password-reset', 'resetpasswordvalid:failed', {
                      ...tokenMeta,
                      elapsedMs: Date.now() - startedAt,
                      status: httpError?.status,
                      errorMessage,
                    });
                  },
                }),
                finalize(() => {
                  logger.log(LoggerContext.UserStore, 'resetpasswordvalid: observable finalized', {
                    ...tokenMeta,
                    elapsedMs: Date.now() - startedAt,
                  });
                  authFlowTrace.record('password-reset', 'resetpasswordvalid:http-finalized', {
                    ...tokenMeta,
                    elapsedMs: Date.now() - startedAt,
                  });
                })
              );
            })
          )
        ),

        updatePassword({ response }: { response: UserResponse; admin: boolean }): void {
          phDialogService.open(UpdatePasswordPopupComponent, {
            data: {
              username: response.userName,
              flowType: 'LEGACY',
              actionType: 'UPDATE',
            },
          });
        },

        clearLocalPortalSession(options?: {
          preservePreviousUser?: boolean;
          preserveOktaTokens?: boolean;
        }): void {
          clearLocalPortalSessionImpl(options);
        },

        logout(options: { davinciLogout: boolean; oktaLogout: boolean; fromLogoutAll?: boolean }): Promise<void> {
          const environment = configService.environment;
          const mode =
            options.oktaLogout && environment.auth.sso ? ('full-slo' as const) : ('portal-only' as const);

          return injector.get(SloLogoutService).executeLogout({
            mode,
            prepareForLogoutAll: !!options.fromLogoutAll,
          });
        },

        resetSession(): void {
          errorService.hideErrorMessage();
          contractSearchStore.resetContractSearchData();
          vehicleStoreRequestsDispatcher.dispatchResetProductForm();
          productStoreRequestsDispatcher.dispatchResetProducts();
          shoppingStepperStore.resetShoppingProgress();
          cartStoreRequestsDispatcher.dispatchResetCart();
        },

        updateVehicleInfo(): void {
          errorService.hideErrorMessage();
          productStoreRequestsDispatcher.dispatchResetProducts();
          cartStoreRequestsDispatcher.dispatchResetCart();
        },

        saveUserDefault: rxMethod<{ markup?: Markup; dealerView?: ProductViewType; sortBy?: SortOptionIdEnum }>(
          pipe(
            switchMap(({ markup, dealerView, sortBy }) => {
              const environment = configService.environment;
              const user = store.user();
              const dealerPtm = supportsDealerProductTypeMarkups(configService);
              const productTypeMarkup = markup?.productTypeMarkup ?? [];
              const markupForState = markup != null ? { ...markup } : undefined;
              const markupForUserApi =
                markupForState != null ? toUserScopedMarkup(markupForState, dealerPtm) : undefined;

              patchState(store, {
                ...(markupForState != null ? { markup: markupForState } : {}),
                ...(user
                  ? {
                      user: {
                        ...user,
                        userDefaults: {
                          ...user.userDefaults,
                          ...(markupForState != null ? { markup: { ...markupForState } } : {}),
                        },
                        ...(sortBy !== undefined || dealerView !== undefined
                          ? {
                              programDefaults: {
                                ...(user.programDefaults ?? {}),
                                ...(sortBy !== undefined ? { sortBy } : {}),
                                ...(dealerView !== undefined ? { dealerView } : {}),
                              },
                            }
                          : {}),
                      },
                    }
                  : {}),
              });
              if (markupForState != null) {
                productStoreRequestsDispatcher.dispatchChangeMarkup(markupForState);
              }

              const userDefault$ = httpClient.post<void>(`${environment.apiUrl}/userdefaultadd`, {
                markup: markupForUserApi,
                dealerView,
                sortBy,
                username: store.user()?.userName || '',
              });

              if (dealerPtm && markupForState != null) {
                return forkJoin([
                  userDefault$,
                  userService.saveDealerDefaults({
                    markup: { productTypeMarkup },
                  }),
                ]).pipe(map(() => undefined));
              }

              return userDefault$;
            })
          )
        ),

        applyUserDefaultsLocal(markup: Markup): void {
          const user = store.user();
          patchState(store, {
            markup: { ...markup },
            ...(user
              ? {
                  user: {
                    ...user,
                    userDefaults: {
                      ...user.userDefaults,
                      markup: { ...markup },
                    },
                  },
                }
              : {}),
          });
          productStoreRequestsDispatcher.dispatchChangeMarkup(markup);
        },

        applyProductTypeMarkupLocal(productTypeMarkup: ProductTypeMarkupEntry[]): void {
          applyProductTypeMarkupLocal(productTypeMarkup);
        },

        loadDealerProductTypeMarkups(): void {
          void firstValueFrom(loadDealerProductTypeMarkups$());
        },

        applyProgramDefaultsLocal(defaults: { sortBy: SortOptionIdEnum; dealerView: ProductViewType }): void {
          const user = store.user();
          if (!user) {
            return;
          }
          patchState(store, {
            user: {
              ...user,
              programDefaults: {
                ...(user.programDefaults ?? {}),
                sortBy: defaults.sortBy,
                dealerView: defaults.dealerView,
              },
            },
          });
        },

        getRecentQuotes: rxMethod<void>(
          pipe(
            switchMap(() => {
              const environment = configService.environment;

              return httpClient
                .post<RecentQuotes[]>(`${environment.apiUrl}/getrecentquote`, {
                  dealer: store.user()?.cmsDealerNumber,
                  application: environment.application,
                })
                .pipe(
                  tapResponse<RecentQuotes[]>({
                    next: (recentQuotes: RecentQuotes[]) => {
                      patchState(store, { recentQuotes });
                    },
                    error: (error: unknown) => {
                      const errorMessage =
                        (error && typeof error === 'object' && 'error' in error
                          ? (error as { error: any }).error
                          : error) || '';
                      logger.log(LoggerContext.UserStore, 'getrecentquote request failed', errorMessage);
                    },
                  })
                );
            })
          )
        ),

        getQuote: rxMethod<{ dealer: string; vin: string }>(
          pipe(
            switchMap((request: { dealer: string; vin: string }) => {
              const environment = configService.environment;
              const user = store.user();
              vehicleStoreRequestsDispatcher.dispatchResetProductForm();
              shoppingStepperStore.resetShoppingProgress();
              cartStoreRequestsDispatcher.dispatchResetCart();

              const useQuoteOnlyEndpoint: boolean = environment.features.useQuoteOnlyEndpoint;
              const url = user?.isRelatedDealer || useQuoteOnlyEndpoint ? `getQuoteOnly` : `getQuote`;

              return httpClient.post<LoadQuote>(`${environment.apiUrl}/${url}`, request).pipe(
                tapResponse<LoadQuote>({
                  next: (quote: LoadQuote) => {
                    const markup = store.markup();
                    const productQuote = mapProductsFromLoadQuote(quote, markup, useQuoteOnlyEndpoint);
                    productStoreRequestsDispatcher.dispatchLoadProductQuote(productQuote);

                    setTimeout(
                      () =>
                        shoppingStepperStore.switchToStepByUrl(ShoppingStepsUrls.CartSummary, { updateProgress: true }),
                      200
                    );
                    cartStoreRequestsDispatcher.dispatchLoadQuoteSuccess(quote);
                  },
                  error: (error: unknown) => {
                    const errorMessage =
                      (error && typeof error === 'object' && 'error' in error
                        ? (error as { error: any }).error
                        : error) || '';
                    logger.log(LoggerContext.UserStore, 'getQuote request failed', errorMessage);

                    patchState(store, { success: false, error: errorMessage || 'Unknown error' });
                  },
                })
              );
            })
          )
        ),

        deleteQuote: rxMethod<{ dealer: string; vin: string }>(
          pipe(
            switchMap(({ dealer, vin }: { dealer: string; vin: string }) => {
              const environment = configService.environment;

              return httpClient.post<RecentQuotes[]>(`${environment.apiUrl}/cartdelete`, { dealer, vin }).pipe(
                tapResponse<RecentQuotes[]>({
                  next: (recentQuotes: RecentQuotes[]) => {
                    patchState(store, { recentQuotes });
                  },
                  error: (error: unknown) => {
                    const errorMessage =
                      (error && typeof error === 'object' && 'error' in error
                        ? (error as { error: any }).error
                        : error) || '';
                    logger.log(LoggerContext.UserStore, 'cartdelete request failed', errorMessage);

                    patchState(store, { success: false, error: 'Login Failure' });
                  },
                })
              );
            })
          )
        ),

        getUsers: rxMethod<void>(
          pipe(
            switchMap(() => {
              const environment = configService.environment;
              const user = store.user();

              return httpClient
                .post<GetUserResponse>(`${environment.apiUrl}/getusers`, {
                  cmsDealerNumber: user?.cmsDealerNumber || '',
                  applicationId: user?.applicationId || '',
                })
                .pipe(
                  tapResponse<GetUserResponse>({
                    next: (userResponse: GetUserResponse) => {
                      patchState(store, {
                        users: userResponse.currentUsers,
                        roles: userResponse.availableRoles,
                      });
                    },
                    error: (error: unknown) => {
                      const errorMessage =
                        (error && typeof error === 'object' && 'error' in error
                          ? (error as { error: any }).error
                          : error) || '';
                      logger.log(LoggerContext.UserStore, 'getusers request failed', errorMessage);
                    },
                  })
                );
            })
          )
        ),

        _updateUserState(updatedUser: UserResponse, isEmulation: boolean): void {
          const environment = configService.environment;

          if (environment.features.isNewTokenAfterEmulation) {
            if (isEmulation) {
              tokenService.setToken(updatedUser.token);
            } else {
              const originalUserToken = updatedUser.token || tokenService.getOriginalUserToken();
              tokenService.setToken(originalUserToken);
            }
          }

          delete updatedUser.token;

          if (isEmulation) {
            patchState(store, {
              originalUser: JSON.parse(JSON.stringify(store.originalUser())),
              user: updatedUser,
              emulatedDealer: true,
            });
          } else {
            patchState(store, {
              user: updatedUser,
              emulatedDealer: false,
            });
          }

          errorService.hideErrorMessage();
          contractSearchStore.resetContractSearchData();
          vehicleStoreRequestsDispatcher.dispatchResetProductForm();
          productStoreRequestsDispatcher.dispatchResetProducts();
          shoppingStepperStore.resetShoppingProgress();
          cartStoreRequestsDispatcher.dispatchResetCart();
          if (supportsDealerProductTypeMarkups(configService)) {
            applyProductTypeMarkupLocal([]);
          }
          void firstValueFrom(loadDealerProductTypeMarkups$());
        },

        resetDealer(updatedUser: UserResponse): void {
          this._updateUserState(updatedUser, false);
        },

        emulateDealer(updatedUser: UserResponse): void {
          this._updateUserState(updatedUser, true);
        },

        updateVersion({ version }: { version: string }): void {
          patchState(store, { appVersion: version });
        },

        updatePendingContractsCount(count: number): void {
          patchState(store, { pendingContractsCount: count });
        },
      };
    }
  )
);
